import { useEffect, useRef } from 'react';

interface Dot {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  hue: 'teal' | 'purple';
  twinklePhase: number;
  flareTimer: number;
  flareCooldown: number;
}

interface Packet {
  fromIdx: number;
  toIdx: number;
  progress: number;
  speed: number;
  hue: 'teal' | 'purple';
}

const TEAL = '20, 224, 176';
const PURPLE = '167, 139, 250';

/**
 * Fullscreen canvas of faint drifting dots with thin connecting lines,
 * twinkling nodes, traveling data packets, and occasional flares.
 * Purely decorative — sits behind content via absolute inset-0.
 */
export default function NetworkBackground({ density = 46 }: { density?: number }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let width = 0;
    let height = 0;
    let dots: Dot[] = [];
    let packets: Packet[] = [];
    let raf = 0;

    const init = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.scale(dpr, dpr);

      const count = Math.round((width * height) / 22000) + density * 0;
      dots = Array.from({ length: Math.max(24, Math.min(70, count)) }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.12,
        vy: (Math.random() - 0.5) * 0.12,
        r: Math.random() * 1.4 + 0.6,
        hue: Math.random() > 0.5 ? 'teal' : 'purple',
        twinklePhase: Math.random() * Math.PI * 2,
        flareTimer: 0,
        flareCooldown: Math.random() * 300 + 200,
      }));
    };

    let time = 0;
    const linkDist = 130;
    const lineBaseOpacity = 0.12;

    const spawnPacket = () => {
      if (dots.length < 4) return;
      const fromIdx = Math.floor(Math.random() * dots.length);
      let toIdx = Math.floor(Math.random() * dots.length);
      let guard = 0;
      while (toIdx === fromIdx && guard < 10) {
        toIdx = Math.floor(Math.random() * dots.length);
        guard++;
      }
      const a = dots[fromIdx];
      const b = dots[toIdx];
      const dx = a.x - b.x;
      const dy = a.y - b.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < linkDist * 1.2) {
        packets.push({
          fromIdx,
          toIdx,
          progress: 0,
          speed: 0.004 + Math.random() * 0.006,
          hue: Math.random() > 0.5 ? 'teal' : 'purple',
        });
      }
    };

    const step = () => {
      time += 1;
      ctx.clearRect(0, 0, width, height);

      // Update dots
      for (const d of dots) {
        d.x += d.vx;
        d.y += d.vy;
        if (d.x < 0 || d.x > width) d.vx *= -1;
        if (d.y < 0 || d.y > height) d.vy *= -1;

        d.flareCooldown -= 1;
        if (d.flareTimer > 0) {
          d.flareTimer -= 1;
        } else if (d.flareCooldown <= 0) {
          if (Math.random() < 0.002) {
            d.flareTimer = 60 + Math.random() * 60;
            d.flareCooldown = 400 + Math.random() * 500;
          }
        }
      }

      // Spawn packets occasionally
      if (Math.random() < 0.03 && packets.length < 6) {
        spawnPacket();
      }

      // Draw connections
      const linePulse = (Math.sin(time * 0.001) + 1) / 2;
      for (let i = 0; i < dots.length; i++) {
        for (let j = i + 1; j < dots.length; j++) {
          const a = dots[i];
          const b = dots[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < linkDist) {
            const opacity = (1 - dist / linkDist) * lineBaseOpacity * (0.75 + linePulse * 0.25);
            ctx.strokeStyle = `rgba(${TEAL}, ${opacity})`;
            ctx.lineWidth = 0.6;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      // Draw packets
      for (let p = packets.length - 1; p >= 0; p--) {
        const pkt = packets[p];
        pkt.progress += pkt.speed;
        if (pkt.progress >= 1) {
          packets.splice(p, 1);
          continue;
        }
        const a = dots[pkt.fromIdx];
        const b = dots[pkt.toIdx];
        if (!a || !b) {
          packets.splice(p, 1);
          continue;
        }
        const px = a.x + (b.x - a.x) * pkt.progress;
        const py = a.y + (b.y - a.y) * pkt.progress;
        const color = pkt.hue === 'teal' ? TEAL : PURPLE;

        // Packet glow
        ctx.beginPath();
        ctx.arc(px, py, 4, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${color}, 0.12)`;
        ctx.fill();

        // Packet core
        ctx.beginPath();
        ctx.arc(px, py, 1.6, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${color}, 0.7)`;
        ctx.fill();

        // Packet head bright spot
        ctx.beginPath();
        ctx.arc(px, py, 0.8, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, 0.9)`;
        ctx.fill();
      }

      // Draw dots
      for (let i = 0; i < dots.length; i++) {
        const d = dots[i];
        const color = d.hue === 'teal' ? TEAL : PURPLE;
        const twinkle = (Math.sin(time * 0.003 + d.twinklePhase) + 1) / 2;
        const baseOpacity = 0.35 + twinkle * 0.35;

        const pulseR = d.r + Math.sin(time * 0.002 + i) * 0.4;

        // Flare glow
        if (d.flareTimer > 0) {
          const flareProgress = Math.sin((d.flareTimer / 120) * Math.PI);
          ctx.beginPath();
          ctx.arc(d.x, d.y, pulseR + 6 * flareProgress, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${color}, ${0.08 * flareProgress})`;
          ctx.fill();
          ctx.beginPath();
          ctx.arc(d.x, d.y, pulseR + 2, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${color}, ${0.25 * flareProgress})`;
          ctx.fill();
        }

        // Core node
        ctx.beginPath();
        ctx.arc(d.x, d.y, Math.max(0.2, pulseR), 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${color}, ${baseOpacity})`;
        ctx.fill();
      }

      raf = requestAnimationFrame(step);
    };

    init();
    step();

    const onResize = () => {
      cancelAnimationFrame(raf);
      packets = [];
      init();
      step();
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
    };
  }, [density]);

  return <canvas ref={canvasRef} className="pointer-events-none fixed inset-0 z-0 h-full w-full" />;
}
